/**
 * Dropdown Component
 *
 * Originally base on the PrimeNG Dropdown. It was directly duplicated because of problems when wrapping
 * the orignal component into an additional form-element and passing through the currently selected
 * model value
 */
import {animate, state, style, transition, trigger} from '@angular/animations';
import {
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ObjectUtils} from 'primeng/components/utils/objectutils';
import {DomHandler, PrimeTemplate, SelectItem} from 'primeng/primeng';

export const DROPDOWN_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true,
};

@Component({
  selector: 'cat-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  animations: [
    trigger('panelState', [
      state('hidden', style({
        opacity: 0,
      })),
      state('visible', style({
        opacity: 1,
      })),
      transition('visible => hidden', animate('400ms ease-in')),
      transition('hidden => visible', animate('400ms ease-out')),
    ]),
  ],
  providers: [DomHandler, ObjectUtils, DROPDOWN_VALUE_ACCESSOR],
})
export class DropdownComponent implements OnInit, AfterViewInit, AfterContentInit, AfterViewChecked, OnDestroy, ControlValueAccessor {

  @Input() scrollHeight = '200px';

  @Input() filter: boolean;

  @Input() fieldsetLabel: string;

  @Input() description: string;

  @Input() name: string;

  @Input() style: any;

  @Input() panelStyle: any;

  @Input() styleClass: string;

  @Input() panelStyleClass: string;

  @Input() disabled: boolean;

  @Input() readonly: boolean;

  @Input() autoWidth = true;

  @Input() required: boolean;

  @Input() editable: boolean;

  @Input() appendTo: any;

  @Input() tabindex: number;

  @Input() placeholder: string;

  @Input() filterPlaceholder: string;

  @Input() inputId: string;

  @Input() dataKey: string;

  @Input() filterBy = 'label';

  @Input() lazy = true;

  @Input() autofocus: boolean;

  @Input() resetFilterOnHide = false;

  @Input() dropdownIcon = 'fa fa-fw fa-caret-down';

  @Input() optionLabel: string;

  @Input() optionValueField: string;

  @Input() emptyFilterMessage = 'No results found';

  @Input() hideLabel: boolean = false;

  @Input() inline: boolean = false;

  @Output() onChange: EventEmitter<any> = new EventEmitter();

  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  @Output() onBlur: EventEmitter<any> = new EventEmitter();

  @ViewChild('container') containerViewChild: ElementRef;

  @ViewChild('panel') panelViewChild: ElementRef;

  @ViewChild('itemswrapper') itemsWrapperViewChild: ElementRef;

  @ViewChild('filter') filterViewChild: ElementRef;

  @ViewChild('in') focusViewChild: ElementRef;

  @ViewChild('editableInput') editableInputViewChild: ElementRef;

  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  public itemTemplate: TemplateRef<any>;

  public selectedItemTemplate: TemplateRef<any>;

  selectedOption: any;

  _options: any[];

  value: any;

  optionsToDisplay: any[];

  hover: boolean;

  focus: boolean;

  public panelVisible = false;

  public shown: boolean;

  public documentClickListener: any;

  public optionsChanged: boolean;

  public panel: HTMLDivElement;

  public container: HTMLDivElement;

  public itemsWrapper: HTMLDivElement;

  public initialized: boolean;

  public selfClick: boolean;

  public itemClick: boolean;

  public hoveredItem: any;

  public selectedOptionUpdated: boolean;

  public filterValue: string;

  get label(): string {
    return (this.selectedOption ? this.selectedOption.label : null);
  }

  constructor(public el: ElementRef, public domHandler: DomHandler, public renderer: Renderer2, private cd: ChangeDetectorRef,
              public objectUtils: ObjectUtils, public zone: NgZone) {
  }

  onModelChange(value?: any) {
  }

  onModelTouched(value?: any) {
  }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;

        case 'selectedItem':
          this.selectedItemTemplate = item.template;
          break;

        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  ngOnInit() {
    this.optionsToDisplay = this.options;
    this.updateSelectedOption(null);
  }

  @Input() get options(): any[] {
    return this._options;
  }

  set options(val: any[]) {
    const opts = this.optionLabel ? this.generateSelectItems(val, this.optionLabel, this.optionValueField) : val;
    this._options = opts;
    this.optionsToDisplay = this._options;
    this.updateSelectedOption(this.value);
    this.optionsChanged = true;

    if (this.filterValue && this.filterValue.length) {
      this.activateFilter();
    }
  }

  private generateSelectItems = function(val, labelField, valueField) {
    let selectItems;
    if (val && val.length) {
      selectItems = [];
      for (let _i = 0, val_1 = val; _i < val_1.length; _i++) {
        const item = val_1[_i];
        const selectItem = {
          label: this.objectUtils.resolveFieldData(item, labelField),
          value: (valueField !== null ? this.objectUtils.resolveFieldData(item, valueField) : item),
        };
        selectItems.push(selectItem);
      }
    }
    return selectItems;
  };

  ngAfterViewInit() {
    this.container = this.containerViewChild.nativeElement as HTMLDivElement;
    this.panel = this.panelViewChild.nativeElement as HTMLDivElement;
    this.itemsWrapper = this.itemsWrapperViewChild.nativeElement as HTMLDivElement;

    if (this.editable) {
      this.updateEditableLabel();
    }

    this.updateDimensions();
    this.initialized = true;

    if (this.appendTo) {
      if (this.appendTo === 'body') {
        document.body.appendChild(this.panel);
      } else {
        this.domHandler.appendChild(this.panel, this.appendTo);
      }
    }
  }

  updateEditableLabel(): void {
    if (this.editableInputViewChild && this.editableInputViewChild.nativeElement) {
      this.editableInputViewChild.nativeElement.value = (this.selectedOption ? this.selectedOption.label : this.value || '');
    }
  }

  onItemClick(event, option) {
    this.itemClick = true;
    this.selectItem(event, option);
    this.focusViewChild.nativeElement.focus();

    this.hide();
  }

  selectItem(event, option) {
    if (this.selectedOption !== option) {
      this.selectedOption = option;
      this.value = option.value;

      this.onModelChange(this.value);
      this.updateEditableLabel();
      this.onChange.emit({
        originalEvent: event,
        value: this.value,
      });
    }

  }

  ngAfterViewChecked() {
    if (this.shown) {
      this.onShow();
      this.shown = false;
    }

    if (this.optionsChanged && this.panelVisible) {
      this.optionsChanged = false;

      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.updateDimensions();
          this.alignPanel();
        }, 1);
      });
    }

    if (this.selectedOptionUpdated && this.itemsWrapper) {
      const selectedItem = this.domHandler.findSingle(this.panel, 'li.ui-state-highlight');
      if (selectedItem) {
        this.domHandler.scrollInView(this.itemsWrapper, this.domHandler.findSingle(this.panel, 'li.ui-state-highlight'));
      }
      this.selectedOptionUpdated = false;
    }
  }

  writeValue(value: any): void {
    if (this.filter) {
      this.resetFilter();
    }

    this.value = value;
    this.updateSelectedOption(value);
    this.updateEditableLabel();
    this.cd.markForCheck();
  }

  resetFilter(): void {
    if (this.filterViewChild && this.filterViewChild.nativeElement) {
      this.filterViewChild.nativeElement.value = '';
    }

    this.optionsToDisplay = this.options;
  }

  updateSelectedOption(val: any): void {
    this.selectedOption = this.findOption(val, this.optionsToDisplay);
    if (!this.placeholder && !this.selectedOption && this.optionsToDisplay && this.optionsToDisplay.length && !this.editable) {
      this.selectedOption = this.optionsToDisplay[0];
    }
    this.selectedOptionUpdated = true;
  }

  registerOnChange(fn: () => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  updateDimensions() {
    // if (this.autoWidth) {
    //   let select = this.domHandler.findSingle(this.el.nativeElement, 'select');
    //   if (!this.style || (!this.style['width'] && !this.style['min-width'])) {
    //     this.el.nativeElement.children[0].style.width = select.offsetWidth + 30 + 'px';
    //   }
    // }
  }

  onMouseclick(event) {
    if (this.disabled || this.readonly) {
      return;
    }

    this.selfClick = true;

    if (!this.itemClick) {
      this.focusViewChild.nativeElement.focus();

      if (this.panelVisible) {
        this.hide();
      } else {
        this.show();

        if (this.filterViewChild !== undefined) {
          setTimeout(() => {
            this.filterViewChild.nativeElement.focus();
          }, 200);
        }
      }
    }
  }

  onEditableInputClick(event) {
    this.itemClick = true;
    this.bindDocumentClickListener();
  }

  onEditableInputFocus(event) {
    this.focus = true;
    this.hide();
  }

  onEditableInputChange(event) {
    this.value = event.target.value;
    this.updateSelectedOption(this.value);
    this.onModelChange(this.value);
    this.onChange.emit({
      originalEvent: event,
      value: this.value,
    });
  }

  onShow() {
    if (this.options && this.options.length) {
      this.alignPanel();
      this.bindDocumentClickListener();

      const selectedListItem = this.domHandler.findSingle(this.itemsWrapper, '.ui-dropdown-item.ui-state-highlight');
      if (selectedListItem) {
        this.domHandler.scrollInView(this.itemsWrapper, selectedListItem);
      }
    }
  }

  show() {
    if (this.appendTo) {
      this.panel.style.minWidth = this.domHandler.getWidth(this.container) + 'px';
    }

    this.panel.style.zIndex = String(++DomHandler.zindex);
    this.panelVisible = true;
    this.shown = true;
  }

  hide() {
    this.panelVisible = false;

    if (this.filter && this.resetFilterOnHide) {
      this.resetFilter();
    }
  }

  alignPanel() {
    if (this.appendTo) {
      this.domHandler.absolutePosition(this.panel, this.container);
    } else {
      this.domHandler.relativePosition(this.panel, this.container);
    }
  }

  onInputFocus(event) {
    this.focus = true;
    this.onFocus.emit(event);
  }

  onInputBlur(event) {
    this.focus = false;
    this.onModelTouched();
    this.onBlur.emit(event);
  }

  onKeydown(event) {
    if (this.readonly) {
      return;
    }

    const selectedItemIndex = this.selectedOption ? this.findOptionIndex(this.selectedOption.value, this.optionsToDisplay) : -1;

    switch (event.which) {
      // down
      case 40:
        if (!this.panelVisible && event.altKey) {
          this.show();
        } else {
          if (selectedItemIndex !== -1) {
            const nextItemIndex = selectedItemIndex + 1;
            if (nextItemIndex != (this.optionsToDisplay.length)) {
              this.selectItem(event, this.optionsToDisplay[nextItemIndex]);
              this.selectedOptionUpdated = true;
            }
          } else if (this.optionsToDisplay) {
            this.selectItem(event, this.optionsToDisplay[0]);
          }
        }

        event.preventDefault();

        break;

      // up
      case 38:
        if (selectedItemIndex > 0) {
          const prevItemIndex = selectedItemIndex - 1;
          this.selectItem(event, this.optionsToDisplay[prevItemIndex]);
          this.selectedOptionUpdated = true;
        }

        event.preventDefault();
        break;

      // space
      case 32:
        if (!this.panelVisible) {
          this.show();
          event.preventDefault();
        }
        break;

      // enter
      case 13:
        this.hide();

        event.preventDefault();
        break;

      // escape and tab
      case 27:
      case 9:
        this.hide();
        break;
    }
  }

  findOptionIndex(val: any, opts: SelectItem[]): number {
    let index = -1;
    if (opts) {
      for (let i = 0; i < opts.length; i++) {
        if ((val == null && opts[i].value == null) || this.objectUtils.equals(val, opts[i].value, this.dataKey)) {
          index = i;
          break;
        }
      }
    }

    return index;
  }

  findOption(val: any, opts: SelectItem[]): SelectItem {
    const index: number = this.findOptionIndex(val, opts);
    return (index !== -1) ? opts[index] : null;
  }

  onFilter(event): void {
    const inputValue = event.target.value.toLowerCase();
    if (inputValue && inputValue.length) {
      this.filterValue = inputValue;
      this.activateFilter();
    } else {
      this.filterValue = null;
      this.optionsToDisplay = this.options;
    }

    this.optionsChanged = true;
  }

  activateFilter() {
    const searchFields: string[] = this.filterBy.split(',');
    if (this.options && this.options.length) {
      this.optionsToDisplay = this.objectUtils.filter(this.options, searchFields, this.filterValue);
      this.optionsChanged = true;
    }
  }

  applyFocus(): void {
    if (this.editable) {
      this.domHandler.findSingle(this.el.nativeElement, '.ui-dropdown-label.ui-inputtext').focus();
    } else {
      this.domHandler.findSingle(this.el.nativeElement, 'input[readonly]').focus();
    }
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.documentClickListener = this.renderer.listen('document', 'click', () => {
        if (!this.selfClick && !this.itemClick) {
          this.panelVisible = false;
          this.unbindDocumentClickListener();
        }

        this.selfClick = false;
        this.itemClick = false;
        this.cd.markForCheck();
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  ngOnDestroy() {
    this.initialized = false;

    this.unbindDocumentClickListener();

    if (this.appendTo) {
      this.el.nativeElement.appendChild(this.panel);
    }
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/ui-form/components/dropdown/dropdown.component.ts